/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * *

Cloudflare Turnstile

Renders the spam-protection widget for every `[data-turnstile]` placeholder
(see templates/partials/forms/turnstile.html and apps/antispam.py).

Cloudflare's own auto-scan only runs once, when its script loads. That is not
enough here: ajax-form.js replaces the form container's HTML on a validation
error (HTTP 206), which destroys the widget and takes the token with it. Tokens
are single-use anyway, so a resubmit after a validation error would fail even if
the markup survived. Hence explicit rendering, re-run whenever the form comes
back from an AJAX round-trip.

*/

const API_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const READY_CALLBACK = 'onTurnstileApiLoaded';
const PLACEHOLDER_SELECTOR = '[data-turnstile]';
const RENDERED_ATTR = 'data-turnstile-widget-id';

let apiRequested = false;

// Widget ids we created, so orphans can be cleaned up after the DOM is replaced.
const widgetIds = [];

function dropOrphanedWidgets() {
    // The container HTML is replaced wholesale, so previously rendered widgets
    // are detached but still tracked by Cloudflare. Tell it to forget them.
    for (let i = widgetIds.length - 1; i >= 0; i -= 1) {
        const id = widgetIds[i];
        if (!document.querySelector(`[${RENDERED_ATTR}="${id}"]`)) {
            try {
                window.turnstile.remove(id);
            } catch (error) {
                // Already gone — nothing to clean up.
            }
            widgetIds.splice(i, 1);
        }
    }
}

function renderWidgets() {
    if (!window.turnstile) {
        return;
    }

    dropOrphanedWidgets();

    document.querySelectorAll(PLACEHOLDER_SELECTOR).forEach((element) => {
        if (element.hasAttribute(RENDERED_ATTR)) {
            return;
        }
        const id = window.turnstile.render(element, {
            sitekey: element.getAttribute('data-sitekey'),
            // Tokens expire after five minutes and these forms are long enough
            // to outlast that, so let the widget fetch a fresh one rather than
            // letting the visitor submit a token the server will reject.
            'refresh-expired': 'auto',
        });
        if (id) {
            element.setAttribute(RENDERED_ATTR, id);
            widgetIds.push(id);
        }
    });
}

function loadApi() {
    if (apiRequested) {
        return;
    }
    apiRequested = true;

    // Explicit mode, so nothing renders until renderWidgets() says so.
    window[READY_CALLBACK] = renderWidgets;

    const script = document.createElement('script');
    script.src = `${API_SRC}?render=explicit&onload=${READY_CALLBACK}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
}

export default function initTurnstile() {
    if (!document.querySelector(PLACEHOLDER_SELECTOR)) {
        return;
    }
    // First call only fetches the script; the API's onload callback does the
    // initial render. Later calls render directly into the fresh markup.
    loadApi();
    renderWidgets();
}

$(() => {
    initTurnstile();

    $(window).on('ajaxForm:success initAjaxForm form-modal:opened softpage:opened', () => {
        initTurnstile();
    });
});
